import { DashboardLegendType } from "@app/dashboard/shared/enums/DashboardLegendType.enum";
import { KeyValue } from "@models/generic/KeyValuePair";
import { Component, Input, OnChanges } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";
import { NgSelectModule } from "@ng-select/ng-select";
import {
    DashboardGraphType,
    DashboardGraphTypeDescription,
} from "../shared/enums/DashboardGraphType.enum";
import { DashboardWidgetModel } from "../shared/models/DashboardWidgetModel";
import { DashboardLegendTypeDescription } from "../shared/enums/DashboardLegendType.enum";
import { GenericListField } from "@app/shared/models/GenericList/GenericListField";
import { WidgetConfigType } from "../shared/enums/WidgetConfigType.enum";
import { catalogKey, translateWidgetCatalogField } from "../shared/translateWidgetCatalogField";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/dashboard/i18n/scope";
import { CerrixTextfieldComponent } from "@app/shared/components/cerrix-textinput/cerrix-textfield/cerrix-textfield.component";
import { CerrixIconPickerComponent } from "@app/shared/components/cerrix-icon-picker/cerrix-icon-picker.component";
import { CheckboxComponent } from "@app/shared/cerrix-checkbox/cerrix-checkbox.component";
import { GenericFieldEditorComponent } from "@app/shared/generic-managers/generic-field-editor/generic-field-editor.component";

@Component({
    selector: "widget-configuration",
    templateUrl: "./widget-configuration.component.html",
    styleUrls: ["./widget-configuration.component.scss"],
    viewProviders: [provideTranslationScope(scope)],
    imports: [
        FormsModule,
        NgSelectModule,
        MatTooltipModule,
        TranslatePipe,
        CerrixTextfieldComponent,
        CerrixIconPickerComponent,
        CheckboxComponent,
        GenericFieldEditorComponent,
    ],
})
export class WidgetConfigurationComponent implements OnChanges {
    private _translation = injectTranslation(scope);

    @Input() config: DashboardWidgetModel;

    additionalData = {};

    widgetSettings: GenericListField[] = [];
    dataFilters: GenericListField[] = [];

    graphItems: KeyValue<DashboardGraphType, string>[] = [];
    legendItems: KeyValue<DashboardLegendType, string>[] = [];

    globalFilterFieldNames = "";

    constructor() {}

    readonly catalogKey = catalogKey;

    async ngOnChanges(): Promise<void> {
        this.reset();
        await this._translation.ready();
        this.setDefaults();
    }

    private reset() {
        this.widgetSettings = [];
        this.dataFilters = [];
        this.graphItems = [];
        this.legendItems = [];
    }

    private setDefaults() {
        if (this.config.customConfig.customTitle === this.config.title) {
            this.config.customConfig.customTitle = "";
        }

        if (!this.config.customConfig.customIcon) {
            this.config.customConfig.customIcon = this.config.icon;
        }

        this.setConfigurations();
        this.setLists();
    }

    filterChanged() {
        if (this.config.api.onFilterChanged) {
            this.config.api.onFilterChanged();
        }
    }

    private setConfigurations() {
        // DashboardWidgetMapping.get() keeps `configurations` by reference (not a deep copy), so
        // clone each field before we mutate it below -- otherwise translateField() would permanently
        // rewrite the shared static catalog for the rest of the app session.
        const clonedConfigurations = this.config.configurations.map((wc) => ({ ...wc }));

        this.widgetSettings = clonedConfigurations.filter(
            (wc) => wc.configType === WidgetConfigType.WidgetConfig
        );

        this.dataFilters = clonedConfigurations.filter(
            (wc) => wc.configType === WidgetConfigType.DataFilter
        );

        // widgetSettings/dataFilters render through the shared generic-field-editor, which has
        // no transloco pipe of its own -- DashboardWidgetMapping's prettyName/description/dropdown
        // Name values are "dashboard.widgetCatalog.*" keys, so resolve them here first.
        [...this.widgetSettings, ...this.dataFilters].forEach((field) =>
            translateWidgetCatalogField(this._translation, field)
        );

        if (this.config.globalFilterKeys && this.config.globalFilterKeys.length > 0) {
            const filtersForGlobal = this.config.globalFilterKeys
                .map((k) => this.dataFilters.find((d) => d.fieldName === k))
                .filter((k) => k)
                .map((k) => k.prettyName);

            this.globalFilterFieldNames = filtersForGlobal.join("\n");
        } else {
            this.globalFilterFieldNames = "";
        }
    }

    private setLists() {
        this.graphItems = this.getList(
            this.config.allowedChartTypes,
            DashboardGraphTypeDescription
        );

        if (this.config.legendConfigurable) {
            this.legendItems = this.getList(
                Object.keys(DashboardLegendTypeDescription).map((k) => <DashboardLegendType>+k),
                DashboardLegendTypeDescription
            );
        } else {
            this.legendItems = [];
        }
    }

    private getList<T>(configItems: T[], descriptions: any): KeyValue<T, string>[] {
        if (!configItems) {
            return [];
        }

        return configItems.map(
            (ci) => new KeyValue<T, string>(ci, this._translation.t(catalogKey(descriptions[ci])))
        );
    }
}
